import { UPLOAD_LIMITS, uploadPercent } from "@rec/shared";
import { api } from "./api";
import { i18n } from "@/i18n";

/**
 * Resumable direct-to-S3 upload.
 *
 * The file is sliced into parts and each is PUT straight to S3, so a dropped
 * connection costs one part rather than the whole upload. That matters at the
 * sizes this accepts: a 3 GB file on a home connection is most of an hour, and
 * a single PUT that fails at 90% starts again from zero.
 *
 * Nothing here passes through our API — it only signs parts. Invariant 7.
 */

/** Three at a time. Enough to saturate a link; few enough not to starve it. */
const CONCURRENCY = 3;
const MAX_PART_ATTEMPTS = 4;

/**
 * Remembered uploads, so closing the tab does not lose progress.
 *
 * `localStorage` rather than IndexedDB deliberately: this stores part numbers
 * and a recording id, a few hundred bytes for even the largest allowed file.
 * The file itself is never stored — the user re-picks it, and we check it is
 * the same one.
 */
const STORE_KEY = "resumable-uploads";
/** Abandoned uploads are aborted server-side; stop offering them long before. */
const RESUME_TTL_MS = 24 * 60 * 60 * 1000;

type Session = {
  recordingId: string;
  /** Identity of the file, so a resume cannot attach parts to a different one. */
  fingerprint: string;
  startedAt: number;
};

/**
 * Name, size and mtime.
 *
 * Not a content hash: hashing 3 GB in the browser to decide whether to resume
 * costs more than the resume saves. This cannot tell apart two files that share
 * all three, which is why the server also checks the assembled size.
 */
function fingerprint(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function readSessions(): Session[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) ?? "[]") as Session[];
    return raw.filter((s) => Date.now() - s.startedAt < RESUME_TTL_MS);
  } catch {
    // A corrupt entry must not make uploading impossible.
    return [];
  }
}

function writeSessions(sessions: Session[]): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(sessions));
  } catch {
    // Full or disabled storage costs the resume, not the upload.
  }
}

function rememberSession(session: Session): void {
  writeSessions([...readSessions().filter((s) => s.recordingId !== session.recordingId), session]);
}

function forgetSession(recordingId: string): void {
  writeSessions(readSessions().filter((s) => s.recordingId !== recordingId));
}

export function findResumable(file: File): Session | undefined {
  return readSessions().find((s) => s.fingerprint === fingerprint(file));
}

/**
 * Read a media file's duration without uploading it.
 *
 * Only the header is decoded, so this is fast even on a multi-gigabyte file,
 * and it means a ten-hour recording is refused before three gigabytes are sent.
 * Resolves undefined rather than rejecting: a browser that cannot decode the
 * container must not block an upload that would have been fine.
 */
export function readDurationMs(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const element = document.createElement("video");

    const done = (value: number | undefined) => {
      URL.revokeObjectURL(url);
      element.remove();
      resolve(value);
    };

    element.preload = "metadata";
    element.onloadedmetadata = () =>
      // Rounded: `duration` is seconds as a float, and the API takes an integer
      // number of milliseconds. Sending 6275091.900000001 failed validation and
      // rejected the upload before a byte moved.
      done(
        Number.isFinite(element.duration)
          ? Math.round(element.duration * 1000)
          : undefined,
      );
    element.onerror = () => done(undefined);
    // Some containers never fire either event. Do not hang the upload on it.
    setTimeout(() => done(undefined), 15_000);
    element.src = url;
  });
}

/** A part's PUT, with progress. `fetch` still cannot report upload progress. */
function putPart(
  url: string,
  blob: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);

    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) onProgress(event.loaded);
    });

    request.addEventListener("load", () => {
      if (request.status < 200 || request.status >= 300) {
        reject(new Error(i18n.t("library:upload.errors.partRejected", { status: request.status })));
        return;
      }

      const etag = request.getResponseHeader("ETag");

      if (!etag) {
        // Invisible to curl and to every server-side test, because only a
        // browser enforces which response headers a script may read. Without
        // `ETag` in the bucket's CORS ExposeHeaders every part uploads
        // perfectly and the upload can never be completed.
        reject(
          new Error(i18n.t("library:upload.errors.noEtag")),
        );
        return;
      }

      resolve(etag);
    });

    request.addEventListener("error", () =>
      reject(new Error(i18n.t("library:upload.errors.connectionDropped"))),
    );
    request.addEventListener("abort", () => reject(new DOMException("Aborted", "AbortError")));

    signal.addEventListener("abort", () => request.abort(), { once: true });
    request.send(blob);
  });
}

export type UploadHandle = {
  recordingId: string;
  promise: Promise<void>;
  cancel: () => void;
};

/**
 * Upload a file, resuming a previous attempt at the same file when there is
 * one.
 *
 * `onProgress` receives 0–100 and accounts for parts already on S3, so a resume
 * starts at the percentage it left off rather than at zero.
 */
export function uploadFile(options: {
  file: File;
  projectId: string | null;
  durationMs?: number;
  resume?: { recordingId: string };
  onProgress: (percent: number) => void;
}): UploadHandle {
  const controller = new AbortController();
  const { file, onProgress } = options;

  let recordingId = options.resume?.recordingId ?? "";

  const promise = (async () => {
    let partBytes = UPLOAD_LIMITS.partBytes;
    // Part number → ETag, for everything S3 already has.
    const done = new Map<number, string>();

    if (options.resume) {
      recordingId = options.resume.recordingId;
      const existing = await api.listUploadedParts(recordingId);
      partBytes = existing.partBytes;
      for (const part of existing.parts) done.set(part.partNumber, part.etag);
    } else {
      const created = await api.presignUpload({
        filename: file.name,
        contentType: file.type || "video/mp4",
        sizeBytes: file.size,
        durationMs: options.durationMs,
        projectId: options.projectId,
      });

      recordingId = created.recordingId;
      partBytes = created.partBytes;

      rememberSession({
        recordingId,
        fingerprint: fingerprint(file),
        startedAt: Date.now(),
      });
    }

    const totalParts = Math.max(1, Math.ceil(file.size / partBytes));
    const pending = Array.from({ length: totalParts }, (_, i) => i + 1).filter(
      (n) => !done.has(n),
    );

    /**
     * Bytes that are definitely on S3.
     *
     * Starts at whatever a previous attempt left there, so a resume does not
     * appear to begin again from zero, and grows as each part completes.
     *
     * Growing it is the whole point. It used to be a constant, and a part's
     * bytes were counted only while that part was in flight — so finishing a
     * part *removed* its contribution and the bar jumped backwards, then
     * forwards as the next part climbed, over and over. The upload was always
     * fine; only the arithmetic was wrong.
     */
    let settled = (totalParts - pending.length) * partBytes;
    const inFlight = new Map<number, number>();

    const report = () => {
      const live = [...inFlight.values()].reduce((a, b) => a + b, 0);
      onProgress(uploadPercent(settled, live, file.size));
    };

    report();

    // Sign in batches — a 3 GB file is 48 parts, and a round trip each would be
    // 48 before the first byte moves.
    const SIGN_BATCH = 25;

    for (let offset = 0; offset < pending.length; offset += SIGN_BATCH) {
      if (controller.signal.aborted) throw new DOMException("Aborted", "AbortError");

      const batch = pending.slice(offset, offset + SIGN_BATCH);
      const signed = await api.signUploadParts(recordingId, batch);
      const urlByPart = new Map(signed.map((s) => [s.partNumber, s.url]));

      // A small worker pool rather than all at once: dozens of concurrent
      // multi-megabyte PUTs starve each other and make progress unreadable.
      let cursor = 0;
      const workers = Array.from(
        { length: Math.min(CONCURRENCY, batch.length) },
        async () => {
          while (cursor < batch.length) {
            const partNumber = batch[cursor++]!;
            const url = urlByPart.get(partNumber);
            if (!url) throw new Error(i18n.t("library:upload.errors.partNotSigned"));

            const start = (partNumber - 1) * partBytes;
            const blob = file.slice(start, Math.min(start + partBytes, file.size));

            let lastError: unknown;

            for (let attempt = 1; attempt <= MAX_PART_ATTEMPTS; attempt++) {
              try {
                const etag = await putPart(
                  url,
                  blob,
                  (loaded) => {
                    inFlight.set(partNumber, loaded);
                    report();
                  },
                  controller.signal,
                );

                done.set(partNumber, etag);
                // Move this part from "in flight" to "settled" in one step, so
                // no report can observe it in neither.
                settled += blob.size;
                inFlight.delete(partNumber);
                report();
                lastError = undefined;
                break;
              } catch (error) {
                if (controller.signal.aborted) throw error;
                lastError = error;
                inFlight.delete(partNumber);
                // Backoff, because an immediate retry into a dropped connection
                // fails the same way.
                await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)));
              }
            }

            if (lastError) throw lastError;
          }
        },
      );

      await Promise.all(workers);
    }

    await api.completeUpload(recordingId);
    forgetSession(recordingId);
    onProgress(100);
  })();

  return {
    get recordingId() {
      return recordingId;
    },
    promise,
    cancel: () => controller.abort(),
  };
}

/**
 * Give up on an upload and release its parts.
 *
 * Parts belonging to no object are stored and billed while appearing in no
 * listing, so this is not optional tidying.
 */
export async function abandonUpload(recordingId: string): Promise<void> {
  forgetSession(recordingId);
  await api.abortUpload(recordingId).catch(() => {
    // The bucket lifecycle rule is the backstop when this cannot run.
  });
}
